import type {
    BidListItemModel,
    DistributionBidListModel,
    ShippingOrderBindModel,
    ShippingOrderModel,
    UserProfileModel,
} from "api";
import { Transition } from "components/shared/column";
import { Select } from "components/shared/form";
import ExternalPrintFormDownloadBtn from "components/shared/externalPrintFormDownloadBtn";
import Loader from "components/shared/loader";
import ShippingOrderInfo from "components/shared/shippingOrderInfo";
import { bidSelectLabel, distributionBidSelectLabel } from "helpers/bid";
import { shippingOrderStatusesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { canDownloadExternalPrintForms } from "helpers/printForms";
import { viewContainer } from "helpers/redux";
import type { RolesModel } from "helpers/roles";
import {
    canDeclineShippingOrderResponseTitle,
    canSignShippingOrderResponseTitle,
    getShippingOrderResponseTitle,
} from "helpers/shippingOrder";
import { useExternalPrintFormDownload } from "hooks/useExternalPrintFormDownload";
import policy from "policies";
import React, { FC, useCallback, useEffect, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Link } from "react-router-dom";
import {
    bindShippingOrder,
    getAvailableDistributionBids,
    getItem,
    getPrintFormFromExternal,
} from "sources/shippingOrders";
import { getAvailableBids } from "sources/waybills";

interface ShippingOrderShowProps {
    match: { params: { id: string } };
    history: { push: (path: string) => void };
    roles: RolesModel;
    profile: UserProfileModel;
    modal: (params: {}) => void;
}

interface BindingSelectProps<T, TRequestParams> {
    inputProps: { name: string };
    url: (params: TRequestParams) => Promise<T[]>;
    propName: (item: T) => string;
    filterPropName: string;
    minimumInputLength: number;
    placeholder: string;
    value: number | null;
    onChange: (payload: { value: number | null; option: T | null }) => void;
    disabled: boolean;
}

const DistributionBidSelect = Select as unknown as FC<
    BindingSelectProps<DistributionBidListModel, Parameters<typeof getAvailableDistributionBids>[0]>
>;
const BidSelect = Select as unknown as FC<BindingSelectProps<BidListItemModel, Parameters<typeof getAvailableBids>[0]>>;

const retryableStatuses = [
    shippingOrderStatusesEnum.FailedToCreateDistributionBid,
    shippingOrderStatusesEnum.ManuallyDistributionBidDetached,
];

const ShippingOrderShow = (props: ShippingOrderShowProps) => {
    const [data, setData] = useState<ShippingOrderModel | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [binding, setBinding] = useState(false);
    const [selectedDistributionBidId, setSelectedDistributionBidId] = useState<number | null>(null);
    const [selectedDistributionBidBidId, setSelectedDistributionBidBidId] = useState<number | null>(null);
    const [selectedBidId, setSelectedBidId] = useState<number | null>(null);
    const shippingOrderId = Number(props.match.params.id);
    const { download: downloadPrintForm, isDownloading: isPrintFormDownloading } = useExternalPrintFormDownload({
        documentType: "shippingOrder",
        requestFn: getPrintFormFromExternal,
    });
    const readOnly = policy("user", {
        user: { roles: props.roles },
        record: {},
        options: {},
    }).isReadOnlyTruckingOrganizationUser();

    const loadData = useCallback(() => {
        setLoading(true);
        setLoadError(false);
        return getItem(shippingOrderId)
            .then((response) => {
                if (!response) {
                    setData(null);
                    setLoadError(true);
                    return null;
                }
                setData(response);
                setSelectedDistributionBidId(response.distributionBidId || null);
                setSelectedDistributionBidBidId(response.bidId || null);
                setSelectedBidId(response.bidId || null);
                return response;
            })
            .catch(() => {
                setData(null);
                setLoadError(true);
                return null;
            })
            .finally(() => setLoading(false));
    }, [shippingOrderId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const orderNumber = data?.title1Content?.orderNumber || data?.details?.orderNumber;
    const createOrderLabel =
        data && retryableStatuses.includes(data.status as (typeof retryableStatuses)[number])
            ? "Повторно создать заказ"
            : "Создать заказ";

    const updateBinding = (params: Omit<ShippingOrderBindModel, "shippingOrderId">) => {
        setBinding(true);
        return bindShippingOrder({ shippingOrderId, ...params })
            .then((success) => {
                if (success) {
                    return loadData();
                }
            })
            .finally(() => setBinding(false));
    };

    const unlink = () =>
        props.modal({
            name: "confirm",
            title: "Удалить связи ЭЗЗ?",
            text: "ЭЗЗ будет отвязана от заказа и заявки.",
            buttons: { confirm: "Отвязать", cancel: "Отмена" },
            action: () => updateBinding({ distributionBidId: null, bidId: null }),
        });

    const openSignModal = (isDecline: boolean) => {
        if (!data) {
            return;
        }
        if (
            isDecline
                ? !canDeclineShippingOrderResponseTitle(data, !readOnly)
                : !canSignShippingOrderResponseTitle(data, !readOnly)
        ) {
            return;
        }
        props.modal({
            name: "signShippingOrderTitle",
            title: isDecline ? "Отказ в согласовании ЭЗЗ" : "Повторная отправка ответного титула ЭЗЗ",
            windowMod: "wide",
            shippingOrder: data,
            shippingOrderTitle: getShippingOrderResponseTitle(data),
            isDecline,
            retrySend: !isDecline,
            powerOfAttorneys: props.profile?.powerOfAttorneys,
            callback: loadData,
        });
    };

    const renderTitleActions = () => {
        if (!data) {
            return null;
        }
        const canRetrySend =
            !readOnly &&
            data.status === shippingOrderStatusesEnum.FailedToSendResponseTitle &&
            Boolean(getShippingOrderResponseTitle(data)?.titleFile?.accessUrlLink);
        const canDecline = canDeclineShippingOrderResponseTitle(data, !readOnly);

        if (!canRetrySend && !canDecline) {
            return null;
        }
        return (
            <div className="d-flex flex-wrap justify-content-end">
                {canRetrySend && (
                    <button
                        type="button"
                        className="btn btn-sm btn-brand mr-2 my-1"
                        disabled={binding || loading}
                        onClick={() => openSignModal(false)}
                    >
                        Повторить отправку T2
                    </button>
                )}
                {canDecline && (
                    <button
                        type="button"
                        className="btn btn-xs btn-outline-danger my-1"
                        disabled={binding || loading}
                        onClick={() => openSignModal(true)}
                    >
                        Отказать в согласовании
                    </button>
                )}
            </div>
        );
    };

    const currentDistributionBidId = data?.distributionBidId || null;
    const currentBidId = data?.bidId || null;
    const distributionBidBindingChanged =
        selectedDistributionBidId !== currentDistributionBidId || selectedDistributionBidBidId !== currentBidId;
    const directBidBindingChanged = selectedBidId !== currentBidId || currentDistributionBidId !== null;

    return (
        <div className="page-content">
            <Transition dir="full">
                <div className="page-book__show book-show">
                    <div className="book-show__head">
                        <button
                            type="button"
                            className="book-show__close close close--sm border-0 bg-transparent"
                            onClick={() => props.history.push(paths.shippingOrders)}
                            aria-label="Вернуться к реестру ЭЗЗ"
                        />
                        <div className="book-show__title">ЭЗЗ {orderNumber || `№${shippingOrderId}`}</div>
                    </div>
                    <div className="book-show__body">
                        <div className="native-scroll__wrap h-100">
                            <Scrollbars>
                                <div className="native-scroll__container shipping-order-show__content py-3">
                                    {loading ? (
                                        <Loader />
                                    ) : loadError || !data ? (
                                        <div className="alert alert-danger" role="alert">
                                            Не удалось открыть ЭЗЗ. Проверьте доступ или попробуйте обновить страницу.
                                        </div>
                                    ) : (
                                        <>
                                            <section className="card mb-4">
                                                <div className="card-header py-2 font-weight-bold">
                                                    Связи с заказом и заявкой
                                                </div>
                                                <div className="card-body">
                                                    <div className="row mb-3">
                                                        <div className="col-md-6">
                                                            <span className="text-muted">Заказ: </span>
                                                            {data.distributionBidId ? (
                                                                <Link
                                                                    to={paths.distributionBidShow(
                                                                        data.distributionBidId,
                                                                    )}
                                                                >
                                                                    №{data.distributionBidId}
                                                                </Link>
                                                            ) : (
                                                                "не привязан"
                                                            )}
                                                        </div>
                                                        <div className="col-md-6">
                                                            <span className="text-muted">Заявка: </span>
                                                            {data.bidId ? (
                                                                <Link to={paths.bidShow("bid", data.bidId)}>
                                                                    №{data.bidId}
                                                                </Link>
                                                            ) : (
                                                                "не привязана"
                                                            )}
                                                        </div>
                                                    </div>

                                                    {!readOnly && (
                                                        <>
                                                            {!data.distributionBidId && !data.bidId && (
                                                                <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                                                                    <span className="text-muted">
                                                                        Можно создать новый заказ на основании ЭЗЗ
                                                                    </span>
                                                                    <Link
                                                                        className="btn btn-sm btn-brand"
                                                                        to={`${paths.distributionBidsNew}?shippingOrderId=${data.id}`}
                                                                    >
                                                                        {createOrderLabel}
                                                                    </Link>
                                                                </div>
                                                            )}

                                                            <div className="form-group border-bottom pb-3">
                                                                <label
                                                                    className="mb-1"
                                                                    htmlFor="shippingOrderDistributionBidIdInput"
                                                                >
                                                                    Привязать к существующему заказу
                                                                </label>
                                                                <div className="d-flex align-items-start">
                                                                    <div className="flex-1 pr-3">
                                                                        <DistributionBidSelect
                                                                            inputProps={{
                                                                                name: "shippingOrderDistributionBidId",
                                                                            }}
                                                                            url={getAvailableDistributionBids}
                                                                            propName={distributionBidSelectLabel}
                                                                            filterPropName="id"
                                                                            minimumInputLength={1}
                                                                            placeholder="Введите номер заказа"
                                                                            value={selectedDistributionBidId}
                                                                            onChange={({
                                                                                value,
                                                                                option,
                                                                            }: {
                                                                                value: number | null;
                                                                                option: DistributionBidListModel | null;
                                                                            }) => {
                                                                                setSelectedDistributionBidId(value);
                                                                                setSelectedDistributionBidBidId(
                                                                                    option?.bidId || null,
                                                                                );
                                                                            }}
                                                                            disabled={binding}
                                                                        />
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-brand"
                                                                        disabled={
                                                                            binding ||
                                                                            selectedDistributionBidId === null ||
                                                                            !distributionBidBindingChanged
                                                                        }
                                                                        onClick={() =>
                                                                            updateBinding({
                                                                                distributionBidId:
                                                                                    selectedDistributionBidId,
                                                                                bidId: selectedDistributionBidBidId,
                                                                            })
                                                                        }
                                                                    >
                                                                        {binding ? "Сохранение..." : "Привязать заказ"}
                                                                    </button>
                                                                </div>
                                                                <div className="small text-muted mt-2">
                                                                    Если по заказу уже создана заявка, ЭЗЗ будет связана
                                                                    и с ней.
                                                                </div>
                                                            </div>

                                                            <div className="form-group mb-0">
                                                                <label
                                                                    className="mb-1"
                                                                    htmlFor="shippingOrderBidIdInput"
                                                                >
                                                                    Привязать напрямую к заявке
                                                                </label>
                                                                <div className="d-flex align-items-start">
                                                                    <div className="flex-1 pr-3">
                                                                        <BidSelect
                                                                            inputProps={{ name: "shippingOrderBidId" }}
                                                                            url={getAvailableBids}
                                                                            propName={bidSelectLabel}
                                                                            filterPropName="id"
                                                                            minimumInputLength={1}
                                                                            placeholder="Введите номер заявки"
                                                                            value={selectedBidId}
                                                                            onChange={({ value }) =>
                                                                                setSelectedBidId(value)
                                                                            }
                                                                            disabled={binding}
                                                                        />
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-brand"
                                                                        disabled={
                                                                            binding ||
                                                                            selectedBidId === null ||
                                                                            !directBidBindingChanged
                                                                        }
                                                                        onClick={() =>
                                                                            updateBinding({
                                                                                distributionBidId: null,
                                                                                bidId: selectedBidId,
                                                                            })
                                                                        }
                                                                    >
                                                                        {binding ? "Сохранение..." : "Привязать заявку"}
                                                                    </button>
                                                                </div>
                                                                <div className="small text-muted mt-2">
                                                                    Прямая привязка к заявке заменит текущую связь с
                                                                    заказом.
                                                                </div>
                                                            </div>

                                                            {(data.distributionBidId || data.bidId) && (
                                                                <div className="d-flex justify-content-end mt-3">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        disabled={binding}
                                                                        onClick={unlink}
                                                                    >
                                                                        Отвязать ЭЗЗ
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </section>
                                            <ShippingOrderInfo
                                                shippingOrder={data}
                                                renderActions={renderTitleActions}
                                                renderPrintFormAction={
                                                    canDownloadExternalPrintForms(props.roles)
                                                        ? () => (
                                                              <ExternalPrintFormDownloadBtn
                                                                  documentType="shippingOrder"
                                                                  loading={isPrintFormDownloading(data.id)}
                                                                  onClick={() => downloadPrintForm(data.id)}
                                                              />
                                                          )
                                                        : null
                                                }
                                            />
                                        </>
                                    )}
                                </div>
                            </Scrollbars>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
    );
};

export default viewContainer(ShippingOrderShow as unknown as FC, "shipping-orders");
